import { useState } from "react";
import { Rss } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "./Logo";
import { useSite } from "../context/SiteContext";
import { API, api, formatApiError } from "../lib/api";

export const Footer = () => {
  const { site } = useSite();
  const s = site?.settings || {};
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/subscribe", { email });
      toast.success("You are inscribed. New essays will find you by candlelight.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative z-10 border-t border-[#232530] bg-[#0d0e12]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-14">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 border-b border-[#232530] pb-12">
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-3">Correspondence</p>
            <h3 className="font-serif font-light text-2xl sm:text-3xl text-[#ede8e1]">Receive new essays by candlelight</h3>
            <p className="mt-2 text-[#a8a29e] font-light">Leave your email — each new essay arrives the moment it is published. No noise, ever.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full md:w-auto gap-3" data-testid="subscribe-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com" data-testid="subscribe-email-input"
              className="flex-1 md:w-72 bg-[#070709] border border-[#232530] px-4 py-3 text-[#ede8e1] font-light placeholder:text-[#57534e] focus:border-[#581313] focus:outline-none transition-colors" />
            <button type="submit" disabled={busy} data-testid="subscribe-submit-button"
              className="bg-[#581313] hover:bg-[#6b1818] disabled:opacity-50 text-[#ede8e1] border border-[#581313]/60 font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors shrink-0">
              {busy ? "Inscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <LogoMark size={34} />
              <span className="font-display text-lg tracking-[0.15em] uppercase text-[#ede8e1]">{s.site_title}</span>
            </div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#78716c]">{s.tagline}</p>
          </div>
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] md:text-right space-y-2">
            <p data-testid="footer-location">{s.location_label}</p>
            <p>
              <a href={`${API}/rss.xml`} target="_blank" rel="noopener noreferrer" data-testid="footer-rss-link"
                className="inline-flex items-center gap-2 hover:text-[#ede8e1] transition-colors">
                <Rss size={13} /> RSS — Follow the writing
              </a>
            </p>
            <p>
              <a href="/colophon" data-testid="footer-colophon-link" className="hover:text-[#ede8e1] transition-colors">
                Colophon
              </a>
            </p>
            <p>MMXXVI — ALL RITES RESERVED</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
